import { useState } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import loginFormValidator from './loginFormValidator';
import getLoginErrors from '../getLoginErrors';

function useLogin() {
  const [isLogIn, setIsLogIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({
    message: '',
    email: '',
    password: '',
    mfa: false
  });
  const [resolver, setResolver] = useState(null);
  const [userCredentials, setUserCredentials] = useState(null);
  const [verificationId, setVerificationId] = useState('');
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [verifyingCode, setVerifyingCode] = useState(false);

  const handleEmailChange = e => {
    if (typeof e === 'string') {
      setEmail(e);
      return;
    }
    setEmail(e.target.value);
    setErrors({ ...errors, email: '' });
  };

  const handlePasswordChange = e => {
    if (typeof e === 'string') {
      setPassword(e);
      return;
    }
    setPassword(e.target.value);
    setErrors({ ...errors, password: '' });
  };

  async function login(_password = null, _email = null) {
    setIsLogIn(true);
    try {
      const { errors: validationErrors, isValid } = loginFormValidator({
        email: _email || email,
        password: _password || password
      });
      if (!isValid) {
        setErrors({
          message: '',
          email: '',
          password: '',
          mfa: false,
          ...validationErrors
        });
        setIsLogIn(false);
        return;
      }
      const userCredentials = await firebase
        .auth()
        .signInWithEmailAndPassword(_email || email, _password || password);
      setUserCredentials(userCredentials);
    } catch (e) {
      if (e.code === 'auth/multi-factor-auth-required') {
        setResolver(e.resolver);
      } else {
        if (e.code === 'auth/user-disabled') {
          alert(
            'This account has been disabled. Please contact support at support@oneillmarketing.net for assistance.'
          );
        }
        setErrors({
          message: '',
          email: '',
          password: '',
          mfa: false,
          ...getLoginErrors(e)
        });
      }
    }
    setIsLogIn(false);
  }

  async function sendCodeToSMS(newPhoneNumber = '') {
    try {
      setVerifyingCode(true);
      let session = null;
      if (resolver) {
        session = resolver.session;
      } else {
        session = await firebase.auth().currentUser.multiFactor.getSession();
      }

      const phoneInfoOptions = {
        session: session
      };

      if (resolver && resolver.hints.length > 0) {
        phoneInfoOptions.multiFactorHint = resolver.hints.at(-1);
      } else {
        phoneInfoOptions.phoneNumber = newPhoneNumber;
      }

      const phoneProvider = new firebase.auth.PhoneAuthProvider();
      const verificationId = await phoneProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifier
      );

      setVerificationId(verificationId);
    } catch (err) {
      console.error('err', err);
      setErrors({ ...errors, message: err.message });
    } finally {
      setVerifyingCode(false);
    }
  }

  return {
    login,
    isLogIn,
    email,
    setEmail: handleEmailChange,
    password,
    setPassword: handlePasswordChange,
    errors,
    resolver,
    userCredentials,
    setUserCredentials,
    verificationId,
    setVerificationId,
    sendCodeToSMS,
    setRecaptchaVerifier,
    verifyingCode
  };
}

export default useLogin;
